// frontend/src/features/ogdb2/components/DeclineAsksModal.jsx
import React, { useEffect, useMemo, useState } from "react";

/**
 * DeclineAsksModal
 * ----------------
 * Purpose:
 * - Provide a single workspace to generate & manage "Decline Rate" asks.
 * - Writes BA facts to backend via onApplyChanges.
 *
 * v1 + Blurb Popup:
 * - Computes a "review score" + reason tags based on currently-loaded row metrics.
 * - Suggests a higher decline_rate ask when score triggers.
 * - Per-row "Blurb" button produces a copy/paste paragraph with lease identifiers and supporting data.
 *
 * IMPORTANT UX CHANGES (per user request):
 * - Do NOT reference BAID anywhere in the consultant-facing blurb (assessors don't care).
 * - Use lease label + Lease Number + RRC for identification.
 * - Do NOT reference "current BA" values. Focus on the ASK and assessor/supporting data only.
 */

function fmt(x) {
  if (x === null || x === undefined || x === "") return "—";
  if (typeof x === "number") return x.toLocaleString();
  return String(x);
}

function fmtPct(x) {
  if (x === null || x === undefined || x === "") return "—";
  const n = typeof x === "number" ? x : Number(String(x).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(x);
  return `${n.toFixed(1)}%`;
}

function toNum(x) {
  if (x === null || x === undefined || x === "") return null;
  if (typeof x === "number") return Number.isFinite(x) ? x : null;
  const n = Number(String(x).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

function clamp(n, lo, hi) {
  if (!Number.isFinite(n)) return n;
  return Math.max(lo, Math.min(hi, n));
}

function round1(n) {
  if (!Number.isFinite(n)) return n;
  return Math.round(n * 10) / 10;
}

function safeFixed(n, d = 2) {
  if (!Number.isFinite(Number(n))) return "—";
  return Number(n).toFixed(d);
}

function firstNonNull(...vals) {
  for (const v of vals) {
    if (v !== null && v !== undefined && v !== "") return v;
  }
  return null;
}

function buildReasonParagraphs(ctx) {
  const parts = [];

  // Strongest evidence first: actual production decline
  if (ctx.yoyDecl !== null && ctx.yoyDecl > 0 && ctx.assDecl !== null && ctx.yoyDecl > ctx.assDecl) {
    parts.push(
      `Actual Production Decline: RRC production data shows this lease declined ${safeFixed(ctx.yoyDecl, 1)}% year-over-year, significantly exceeding the assessor's assumed decline rate of ${safeFixed(ctx.assDecl, 1)}%. The actual production history demonstrates that the assessor's decline assumption understates the rate at which this lease is depleting.`
    );
  } else if (ctx.yoyDecl !== null && ctx.yoyDecl > 0) {
    parts.push(
      `Actual Production Decline: RRC production data shows this lease declined ${safeFixed(ctx.yoyDecl, 1)}% year-over-year.`
    );
  }

  // Intra-year trend
  if (ctx.trend !== null && ctx.trend < -15) {
    parts.push(
      `Production Trend: Monthly production within the year shows an annualized trend of ${safeFixed(ctx.trend, 1)}%, indicating ${ctx.trend < -30 ? "accelerating" : "continued"} decline within the production year.`
    );
  }

  // Start rate gap
  if (ctx.srGapPct !== null && ctx.srGapPct > 10) {
    const srLabel = ctx.useGas ? "gas" : "oil";
    parts.push(
      `Start Rate Discrepancy: Third-party ${srLabel} start rate of ${fmt(ctx.tpSR)} is ${safeFixed(ctx.srGapPct, 1)}% below the assessor's comparable start rate of ${fmt(ctx.assSR)}. The assessor appears to overstate initial production, compounding the effect of an understated decline.`
    );
  }

  if (ctx.econLife !== null && ctx.econLife <= 3) {
    parts.push(
      `Economic Life Support: The assessor inputs show an economic life of ${safeFixed(
        ctx.econLife,
        0
      )} years, which indicates the property is expected to reach economic limit quickly. A flatter decline profile is not consistent with that short remaining life.`
    );
  }

  if (ctx.rli !== null && ctx.rli < 2.5) {
    const prodLabel = ctx.useGas ? "Year-1 Gas Production" : "Year-1 Oil Production";
    const resLabel = ctx.useGas ? "Economic Gas Reserves" : "Economic Oil Reserves";
    const srcNote = ctx.prodSource === "third_party" ? " (per RRC/third-party data)" : ctx.prodSource === "actual" ? " (per client data)" : "";
    parts.push(
      `Reserve-Life Support: ${resLabel} of ${fmt(ctx.econRes)} divided by ${prodLabel} of ${fmt(
        ctx.year1Prod
      )}${srcNote} implies an RLI of ${safeFixed(ctx.rli, 2)} years. This short reserve-life profile (RLI < 2.5) supports a higher decline assumption / earlier economic exhaustion than a flat curve.`
    );
  }

  if (ctx.loeRatio !== null && ctx.loeRatio > 0.4) {
    const revNote = ctx.prodSource && ctx.prodSource !== "assessor" ? ` (revenue proxy uses ${ctx.prodSource === "third_party" ? "RRC/third-party" : "client"} production)` : "";
    parts.push(
      `Economic Stress Support: First-year LOE of ${fmt(ctx.loe)} relative to first-year revenue proxy of ${fmt(
        ctx.rev
      )} implies LOE/Revenue of ${safeFixed(ctx.loeRatio, 2)}${revNote}. Elevated LOE burden increases economic limit risk and supports accelerating decline / earlier economic exhaustion.`
    );
  }

  return parts;
}

export default function DeclineAsksModal({
  open,
  onClose,
  rows, // filteredSortedRows (visible rows)
  taxYear,
  getCell, // function(row, colKey) -> value (BulkAnalysisPage's getCell)
  onApplyChanges, // async ({ actions }) => void ; actions: [{ unitId, setTo: number|null }]
}) {
  const [tab, setTab] = useState("flagged"); // flagged | all | included
  const [draft, setDraft] = useState({}); // unitId -> { include, suggested, reasons, score }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // Blurb popup
  const [blurbOpen, setBlurbOpen] = useState(false);
  const [blurbText, setBlurbText] = useState("");
  const [blurbTitle, setBlurbTitle] = useState("");

  const units = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((r) => ({
        row: r,
        unitId: r?.unit?.id, // internal only (not shown to assessor)
        label:
          r?.unit?.well_name ||
          r?.unit?.display_name ||
          r?.unit?.lease_name ||
          r?.unit?.rrc_number ||
          r?.unit?.id,
      }))
      .filter((x) => Number.isFinite(Number(x.unitId)));
  }, [rows]);

  const computed = useMemo(() => {
    const out = [];

    for (const u of units) {
      const r = u.row;
      const unitId = Number(u.unitId);

      // Identifiers (assessor-facing)
      const leaseLabel = String(u.label || "").trim() || "—";
      const leaseNumber = firstNonNull(
        getCell(r, "m.lease_number.assessor"),
        r?.unit?.lease_number,
        r?.unit?.leaseNumber,
        null
      );

      const rrcNumber = firstNonNull(
        r?.unit?.rrc_number,
        getCell(r, "m.rrc_number.assessor"),
        getCell(r, "m.rrc.assessor"),
        null
      );

      // Assessor decline: prefer decline_rate, fallback to gas/oil-specific.
      let assDecl = toNum(getCell(r, "m.decline_rate.assessor"));
      if (assDecl === null) {
        const gasDecl = toNum(getCell(r, "m.gas_decline_rate.assessor"));
        const oilDecl = toNum(getCell(r, "m.oil_decline_rate.assessor"));
        assDecl = firstNonNull(gasDecl, oilDecl, null);
      }

      // Inputs for rules
      const econLife = toNum(getCell(r, "m.economic_life_years.assessor"));
      const loe = toNum(getCell(r, "m.loe.assessor"));

      // Production: assessor first, fall back to third_party (RRC/GasSearch), then actual
      const gasVol = firstNonNull(
        toNum(getCell(r, "m.gas_mcf.assessor")),
        toNum(getCell(r, "m.gas_mcf.third_party")),
        toNum(getCell(r, "m.gas_mcf.actual")),
      );
      const oilVol = firstNonNull(
        toNum(getCell(r, "m.oil_bbl.assessor")),
        toNum(getCell(r, "m.oil_bbl.third_party")),
        toNum(getCell(r, "m.oil_bbl.actual")),
      );
      const prodSource =
        toNum(getCell(r, "m.gas_mcf.assessor")) || toNum(getCell(r, "m.oil_bbl.assessor"))
          ? "assessor"
          : toNum(getCell(r, "m.gas_mcf.third_party")) || toNum(getCell(r, "m.oil_bbl.third_party"))
          ? "third_party"
          : toNum(getCell(r, "m.gas_mcf.actual")) || toNum(getCell(r, "m.oil_bbl.actual"))
          ? "actual"
          : null;

      const gasRes = firstNonNull(
        toNum(getCell(r, "m.gas_reserves_economic.assessor")),
        toNum(getCell(r, "m.gas_reserves_economic.third_party")),
      );
      const oilRes = firstNonNull(
        toNum(getCell(r, "m.oil_reserves_economic.assessor")),
        toNum(getCell(r, "m.oil_reserves_economic.third_party")),
      );

      const gasPrice = toNum(getCell(r, "m.gas_price.assessor"));
      const oilPrice = toNum(getCell(r, "m.oil_price.assessor"));

      const useGas = (gasRes !== null && gasVol !== null) || (gasVol !== null && oilVol === null);
      const year1Prod = useGas ? gasVol : oilVol;
      const econRes = useGas ? gasRes : oilRes;
      const price = useGas ? gasPrice : oilPrice;

      const rli =
        econRes !== null && year1Prod !== null && year1Prod > 0 ? econRes / year1Prod : null;

      const rev =
        year1Prod !== null && price !== null && year1Prod > 0 ? year1Prod * price : null;

      const loeRatio = loe !== null && rev !== null && rev > 0 ? loe / rev : null;

      // Skip equipment-value-floor BAIDs (0% assessor decline = valued on equipment, not income)
      if (assDecl !== null && assDecl === 0) {
        continue;
      }

      // --- New decline analytics from RRC (third_party facts) ---
      const yoyOilDecl = toNum(getCell(r, "m.tp_oil_yoy_decline_pct.third_party"));
      const yoyGasDecl = toNum(getCell(r, "m.tp_gas_yoy_decline_pct.third_party"));
      const yoyDecl = useGas ? yoyGasDecl : (yoyOilDecl ?? yoyGasDecl);

      const trendOil = toNum(getCell(r, "m.tp_oil_trend_pct.third_party"));
      const trendGas = toNum(getCell(r, "m.tp_gas_trend_pct.third_party"));
      const trend = useGas ? trendGas : (trendOil ?? trendGas);
      // trend is negative when production is dropping intra-year

      // 3P start rate vs assessor start rate gap
      const ass3pOilSR = toNum(getCell(r, "m.comparable_oil_start_rate.assessor"));
      const ass3pGasSR = toNum(getCell(r, "m.comparable_gas_start_rate.assessor"));
      const tp3pOilSR = firstNonNull(
        toNum(getCell(r, "m.tp_oil_sr_sep_dec.third_party")),
        toNum(getCell(r, "m.tp_oil_sr_last_4.third_party")),
      );
      const tp3pGasSR = firstNonNull(
        toNum(getCell(r, "m.tp_gas_sr_sep_dec.third_party")),
        toNum(getCell(r, "m.tp_gas_sr_last_4.third_party")),
      );
      const assSR = useGas ? ass3pGasSR : ass3pOilSR;
      const tpSR = useGas ? tp3pGasSR : tp3pOilSR;
      // srGapPct: positive means 3P shows LOWER production than assessor (beneficial for us)
      const srGapPct = (assSR && tpSR && assSR > 0)
        ? round1((1 - tpSR / assSR) * 100)
        : null;

      // ---- Scoring (v2) ----
      // Each factor contributes points. Total drives the suggested adjustment.
      const reasons = [];
      let score = 0;

      // Factor 1: Actual YoY decline exceeds assessor's assumed decline
      if (yoyDecl !== null && yoyDecl > 0) {
        if (assDecl !== null && yoyDecl > assDecl) {
          const gap = round1(yoyDecl - assDecl);
          score += Math.min(4, Math.round(gap / 3)); // ~1 pt per 3% gap, max 4
          reasons.push(`Actual YoY decline ${round1(yoyDecl)}% > assessor ${round1(assDecl)}% (+${gap}%)`);
        } else if (assDecl !== null) {
          // Actual decline exists but doesn't exceed assessor — still informative
          reasons.push(`Actual YoY decline ${round1(yoyDecl)}% (assessor: ${round1(assDecl)}%)`);
        }
      }

      // Factor 2: Intra-year production trend is declining
      if (trend !== null && trend < -15) {
        score += 1;
        reasons.push(`Intra-year trend: ${round1(trend)}% annualized`);
        if (trend < -30) {
          score += 1;
          reasons.push("Accelerating intra-year decline");
        }
      }

      // Factor 3: 3P start rate lower than assessor (assessor overestimates initial production)
      if (srGapPct !== null && srGapPct > 10) {
        score += Math.min(2, Math.round(srGapPct / 15)); // ~1 pt per 15% gap, max 2
        reasons.push(`3P start rate ${round1(srGapPct)}% below assessor`);
      }

      // Factor 4: Short economic life
      if (econLife !== null && econLife <= 3) {
        score += 2;
        reasons.push("Short economic life (≤3 yrs)");
      }

      // Factor 5: Low RLI
      if (rli !== null && rli < 2.5) {
        score += 2;
        reasons.push(`Low reserve-life (RLI ${safeFixed(rli, 1)})`);
      }

      // Factor 6: LOE stress
      if (loeRatio !== null && loeRatio > 0.4) {
        score += 1;
        reasons.push(`LOE stress (LOE/Rev ${safeFixed(loeRatio, 2)})`);
        if (loeRatio > 0.7) {
          score += 1;
          reasons.push("Severe LOE stress (>0.70)");
        }
      }

      // ---- Suggested ask (v2) ----
      // Base: use actual YoY decline if available and higher than assessor.
      // Then add score-based bump for economic stress factors.
      let suggested = null;
      const stressBump = round1(score * 1.0); // 1% per score point

      if (assDecl !== null) {
        // Start from the higher of assessor or actual YoY decline
        let base = assDecl;
        if (yoyDecl !== null && yoyDecl > assDecl) {
          base = round1(yoyDecl);
        }
        // Add stress bump, but never go below assessor
        suggested = round1(Math.max(assDecl, base + stressBump));
        // If suggestion equals assessor, no ask needed
        if (suggested <= assDecl) suggested = assDecl;
      } else {
        // No assessor decline — use actual YoY + bump if we have data
        if (yoyDecl !== null && yoyDecl > 0) {
          suggested = round1(yoyDecl + stressBump);
        } else if (score >= 3) {
          suggested = round1(10 + stressBump);
        }
      }

      const defaultInclude = suggested !== null && assDecl !== null && suggested > assDecl;

      out.push({
        unitId, // internal only
        leaseLabel,
        leaseNumber,
        rrcNumber,

        score,
        reasons,
        assDecl,
        suggested,
        rli,
        loeRatio,

        // context for blurb
        econLife,
        loe,
        year1Prod,
        econRes,
        price,
        rev,
        useGas,
        prodSource,
        yoyDecl,
        trend,
        srGapPct,
        assSR,
        tpSR,

        defaultInclude,
      });
    }

    out.sort((a, b) => (b.score - a.score) || String(a.leaseLabel).localeCompare(String(b.leaseLabel)));
    return out;
  }, [units, getCell]);

  useEffect(() => {
    if (!open) return;
    setMsg("");
    setErr("");

    const next = {};
    for (const c of computed) {
      next[c.unitId] = {
        include: !!c.defaultInclude,
        suggested: c.suggested,
        reasons: c.reasons,
        score: c.score,
      };
    }
    setDraft(next);
  }, [open, computed]);

  const filteredList = useMemo(() => {
    const list = computed || [];
    if (tab === "included") return list.filter((x) => !!draft?.[x.unitId]?.include);
    if (tab === "flagged") return list.filter((x) => (draft?.[x.unitId]?.score || 0) >= 4);
    return list;
  }, [computed, draft, tab]);

  async function apply() {
    setMsg("");
    setErr("");

    const actions = [];
    for (const c of computed) {
      const d = draft?.[c.unitId];
      if (!d) continue;
      const setTo = d.include ? toNum(d.suggested) : null;
      actions.push({ unitId: c.unitId, setTo });
    }

    const includeCount = actions.filter((a) => a.setTo !== null).length;

    const ok = window.confirm(
      `Apply Decline Asks will write decline_rate asks for ${includeCount} units (and clear it for the rest shown in this modal).\n\nContinue?`
    );
    if (!ok) return;

    setBusy(true);
    try {
      await Promise.resolve(onApplyChanges?.({ actions }));
      setMsg("Saved. (Decline asks updated.)");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to save decline asks.");
    } finally {
      setBusy(false);
    }
  }

  function identifiersLine(c) {
    const bits = [];
    if (c.leaseNumber) bits.push(`Lease # ${String(c.leaseNumber).replace(/,/g, "")}`);
    if (c.rrcNumber) bits.push(`RRC ${String(c.rrcNumber).replace(/,/g, "")}`);
    return bits.length ? bits.join(" • ") : "";
  }

  function openBlurbForRow(c) {
    const d = draft?.[c.unitId] || {};
    const suggested = toNum(d.suggested ?? c.suggested);
    const include = !!d.include;

    const idLine = identifiersLine(c);

    const headerLines = [
      `Decline Ask Summary (Copy/Paste)`,
      `Lease: ${c.leaseLabel}`,
      ...(idLine ? [idLine] : []),
      `Tax Year: ${fmt(taxYear)}`,
      ``,
    ];

    const keyDataLines = [
      `Ask:`,
      `- Requested Decline Rate: ${suggested === null ? "—" : `${round1(Number(suggested)).toFixed(1)}%`}`,
      ``,
      `Assessor Inputs (support):`,
      `- Assessor Decline (primary): ${fmtPct(c.assDecl)}`,
      c.econLife !== null ? `- Economic Life (assessor): ${safeFixed(c.econLife, 0)} yrs` : `- Economic Life (assessor): —`,
      c.rli !== null ? `- RLI (Economic Reserves / Year-1 Prod): ${safeFixed(c.rli, 2)}` : `- RLI: —`,
      c.loeRatio !== null ? `- LOE/Revenue Proxy: ${safeFixed(c.loeRatio, 2)}` : `- LOE/Revenue Proxy: —`,
      ``,
    ];

    const productLabel = c.useGas ? "Gas" : "Oil";
    const prodUnit = c.useGas ? "MCF" : "BBL";
    const resUnit = c.useGas ? "MCF" : "BBL";
    const prodSourceLabel = c.prodSource === "third_party" ? " (RRC/GasSearch)" : c.prodSource === "actual" ? " (client)" : " (assessor)";

    const supportLines = [
      `Supporting Data (lease-specific):`,
      c.yoyDecl !== null ? `- Actual YoY Decline (RRC): ${safeFixed(c.yoyDecl, 1)}%` : `- Actual YoY Decline (RRC): —`,
      c.trend !== null ? `- Intra-Year Trend (annualized): ${safeFixed(c.trend, 1)}%` : `- Intra-Year Trend: —`,
      c.srGapPct !== null ? `- 3P vs Assessor Start Rate Gap: ${safeFixed(c.srGapPct, 1)}% (3P: ${fmt(c.tpSR)}, Assessor: ${fmt(c.assSR)})` : null,
      c.year1Prod !== null ? `- Year-1 ${productLabel} Production${prodSourceLabel}: ${fmt(c.year1Prod)} ${prodUnit}` : `- Year-1 ${productLabel} Production: —`,
      c.econRes !== null ? `- Economic ${productLabel} Reserves: ${fmt(c.econRes)} ${resUnit}` : `- Economic ${productLabel} Reserves: —`,
      c.price !== null ? `- Price (assessor): ${fmt(c.price)}` : `- Price (assessor): —`,
      c.loe !== null ? `- LOE (assessor): ${fmt(c.loe)}` : `- LOE (assessor): —`,
      c.rev !== null ? `- Revenue Proxy (Year-1 Prod × Price): ${fmt(c.rev)}` : `- Revenue Proxy: —`,
      ``,
    ].filter(Boolean);

    const reasons = (d.reasons || c.reasons || []).slice(0);
    const reasonLines = [
      `Why this qualifies:`,
      ...(reasons.length ? reasons.map((x) => `- ${x}`) : [`- (No rule triggers recorded)`]),
      ``,
    ];

    const reasonParagraphs = buildReasonParagraphs({
      econLife: c.econLife,
      rli: c.rli,
      loeRatio: c.loeRatio,
      year1Prod: c.year1Prod,
      econRes: c.econRes,
      useGas: c.useGas,
      loe: c.loe,
      rev: c.rev,
      prodSource: c.prodSource,
      yoyDecl: c.yoyDecl,
      trend: c.trend,
      srGapPct: c.srGapPct,
      assSR: c.assSR,
      tpSR: c.tpSR,
      assDecl: c.assDecl,
    });

    const paragraphBlock = [
      `Narrative (ready to paste):`,
      `We request an adjustment to the decline assumption for ${c.leaseLabel}${idLine ? ` (${idLine})` : ""} for Tax Year ${fmt(
        taxYear
      )}. Based on the assessor's own inputs, the current decline assumption (${fmtPct(
        c.assDecl
      )}) is not consistent with the lease's implied remaining economic performance. We request a decline rate of ${
        suggested === null ? "—" : `${round1(Number(suggested)).toFixed(1)}%`
      } for purposes of the valuation forecast.`,
      ``,
      ...reasonParagraphs.map((p) => `${p}\n`),
      `Request: Please update the decline assumption (primary decline_rate) to ${
        suggested === null ? "—" : `${round1(Number(suggested)).toFixed(1)}%`
      } for this lease for purposes of the valuation forecast.`,
      ``,
      `Status in Decline Asks modal: ${include ? "Included" : "Not included"}`,
    ];

    const text = [
      ...headerLines,
      ...keyDataLines,
      ...supportLines,
      ...reasonLines,
      ...paragraphBlock,
    ].join("\n");

    setBlurbTitle(`Blurb — ${c.leaseLabel}${idLine ? ` — ${idLine}` : ""}`);
    setBlurbText(text);
    setBlurbOpen(true);
  }

  async function copyBlurb() {
    try {
      await navigator.clipboard.writeText(String(blurbText || ""));
      setMsg("Blurb copied to clipboard.");
      setTimeout(() => setMsg(""), 1200);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = String(blurbText || "");
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
        setMsg("Blurb copied to clipboard.");
        setTimeout(() => setMsg(""), 1200);
      } catch {
        setErr("Copy failed. You can manually select and copy the text in the blurb box.");
      }
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-[96vw] h-[92vh] rounded-2xl bg-white shadow-xl border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-gray-800">Decline Asks</div>
            <div className="text-xs text-gray-500 mt-1">
              Generates "Decline Rate" asks from the currently visible Analysis rows, then writes decline ask values to the backend.
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button className="btn" type="button" onClick={onClose} disabled={busy}>
              Close
            </button>
          </div>
        </div>

        <div className="p-4 flex-1 flex flex-col overflow-hidden space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              className={["btn", tab === "flagged" ? "btn-primary" : ""].join(" ")}
              type="button"
              onClick={() => setTab("flagged")}
              disabled={busy}
            >
              Flagged (score ≥ 4)
            </button>
            <button
              className={["btn", tab === "included" ? "btn-primary" : ""].join(" ")}
              type="button"
              onClick={() => setTab("included")}
              disabled={busy}
            >
              Included
            </button>
            <button
              className={["btn", tab === "all" ? "btn-primary" : ""].join(" ")}
              type="button"
              onClick={() => setTab("all")}
              disabled={busy}
            >
              All
            </button>

            <div className="ml-auto text-xs text-gray-500">
              Tax Year: <span className="font-semibold">{fmt(taxYear)}</span>
            </div>
          </div>

          <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-gray-700">
            <div className="font-semibold text-gray-800 mb-1">How this works</div>
            <div>
              Suggested decline starts from the higher of the assessor&apos;s rate or actual YoY decline (from RRC data), then adds 1% per scoring point for economic stress
              (short RLI, LOE burden, intra-year trend, start rate gap). &quot;Actual YoY&quot; column highlights in red when it exceeds the assessor.
            </div>
          </div>

          {msg ? <div className="text-xs text-green-700">{msg}</div> : null}
          {err ? <div className="text-xs text-red-700">{err}</div> : null}

          <div className="flex-1 rounded-2xl border border-gray-200 overflow-auto">
            <table className="min-w-[1400px] w-full text-sm border-separate border-spacing-0">
              <thead className="bg-gray-50 text-gray-600 text-left sticky top-0">
                <tr>
                  <th className="py-2 px-3">Include</th>
                  <th className="py-2 px-3">Lease</th>
                  <th className="py-2 px-3">Lease # / RRC</th>
                  <th className="py-2 px-3">Score</th>
                  <th className="py-2 px-3">Reasons</th>
                  <th className="py-2 px-3">Assessor Decline</th>
                  <th className="py-2 px-3">Actual YoY</th>
                  <th className="py-2 px-3">Requested Decline</th>
                  <th className="py-2 px-3">RLI</th>
                  <th className="py-2 px-3">LOE/Rev</th>
                  <th className="py-2 px-3">Blurb</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.length === 0 ? (
                  <tr>
                    <td className="py-3 px-3 text-gray-500" colSpan={11}>
                      No rows in this view.
                    </td>
                  </tr>
                ) : (
                  filteredList.map((c) => {
                    const d = draft?.[c.unitId] || {};
                    const idLine = identifiersLine(c);

                    return (
                      <tr key={c.unitId} className="border-t hover:bg-gray-50">
                        <td className="py-2 px-3">
                          <input
                            type="checkbox"
                            checked={!!d.include}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...(prev || {}),
                                [c.unitId]: { ...(prev?.[c.unitId] || {}), include: e.target.checked },
                              }))
                            }
                            disabled={busy}
                          />
                        </td>
                        <td className="py-2 px-3">{fmt(c.leaseLabel)}</td>
                        <td className="py-2 px-3">{idLine || "—"}</td>
                        <td className="py-2 px-3">{fmt(c.score)}</td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap gap-1">
                            {(d.reasons || c.reasons || []).slice(0, 4).map((x, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                              >
                                {x}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 px-3">{fmtPct(c.assDecl)}</td>
                        <td className="py-2 px-3">
                          {c.yoyDecl !== null ? (
                            <span className={c.yoyDecl > (c.assDecl || 0) ? "text-red-600 font-semibold" : ""}>
                              {fmtPct(c.yoyDecl)}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="py-2 px-3">
                          <input
                            className="w-[120px] rounded-xl border border-gray-200 px-2 py-1"
                            value={d.suggested ?? ""}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...(prev || {}),
                                [c.unitId]: { ...(prev?.[c.unitId] || {}), suggested: e.target.value },
                              }))
                            }
                            disabled={busy}
                            title="Edit requested decline_rate"
                          />
                        </td>
                        <td className="py-2 px-3">
                          {c.rli === null || c.rli === undefined ? "—" : Number(c.rli).toFixed(2)}
                        </td>
                        <td className="py-2 px-3">
                          {c.loeRatio === null || c.loeRatio === undefined ? "—" : Number(c.loeRatio).toFixed(2)}
                        </td>
                        <td className="py-2 px-3">
                          <button
                            type="button"
                            className="btn"
                            onClick={() => openBlurbForRow(c)}
                            disabled={busy}
                            title="Open a copy/paste paragraph for this lease"
                          >
                            Blurb
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button className="btn" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" type="button" onClick={apply} disabled={busy}>
            {busy ? "Applying…" : "Apply Decline Asks"}
          </button>
        </div>

        {/* Blurb Popup */}
        {blurbOpen ? (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
            <div className="w-[92vw] h-[90vh] rounded-2xl bg-white shadow-xl border border-gray-200 flex flex-col">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-800 truncate">{blurbTitle}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Copy/paste this into an evidence packet or email. You can edit before sending.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="btn" type="button" onClick={copyBlurb}>
                    Copy
                  </button>
                  <button
                    className="btn"
                    type="button"
                    onClick={() => {
                      setBlurbOpen(false);
                      setBlurbText("");
                      setBlurbTitle("");
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="p-4 flex-1 overflow-auto">
                <textarea
                  className="w-full h-full rounded-xl border border-gray-200 p-3 font-mono text-xs"
                  style={{ minHeight: "360px" }}
                  value={blurbText}
                  onChange={(e) => setBlurbText(e.target.value)}
                />
              </div>

              <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
                <button className="btn" type="button" onClick={copyBlurb}>
                  Copy
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    setBlurbOpen(false);
                    setBlurbText("");
                    setBlurbTitle("");
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}